
import React, { useState, useCallback } from 'react';
import { Quest, QuestCategory } from '@/types/quests';
import { useApp } from '@/context/AppContext';

// Import UI components
import Header from '@/components/Header';
import ActiveQuestsPanel, { PostTokenRoutinePanel } from '@/components/ActiveQuestsPanel';
import StatsPanel from '@/components/StatsPanel';
import CalendarView from '@/components/CalendarView';
import QuestDetailsModal from '@/components/QuestDetailsModal';
import EditQuestModal from '@/components/EditQuestModal';
import SettingsModal from '@/components/SettingsModal';
import { QUEST_REWARD, CATEGORY_ORDER } from '../constants';
import { getTodayDateString } from '../utils/date';
import TokenDisplay from '@/components/TokenDisplay';
import QuestSelection from '@/components/QuestArea';

const MainScreen: React.FC = () => {
  const { quests, daily, settings, isLoaded, version } = useApp();

  const [detailedQuest, setDetailedQuest] = useState<Quest | null>(null);
  const [editingQuest, setEditingQuest] = useState<Quest | null>(null);
  const [isSettingsModalOpen, setSettingsModalOpen] = useState<boolean>(false);

  const handleCompleteQuest = useCallback((quest: Quest) => {
    const today = getTodayDateString();
    quests.setActiveQuests(prev => prev.filter(q => q.id !== quest.id));
    
    if (!daily.questTokenEarnedToday) {
      daily.setTokens(t => t + QUEST_REWARD);
      daily.setQuestTokenEarnedToday(true);
      const todayHistory = daily.history[today] || { tokens: daily.tokens + QUEST_REWARD, questsCompleted: [] };
      daily.updateHistory(today, { ...todayHistory, tokens: todayHistory.tokens + QUEST_REWARD });
    }
    
    quests.setCompletedQuestIds(prev => {
      if (prev.includes(quest.id)) return prev;
      return [...prev, quest.id];
    });

    const todayHistory = daily.history[today] || { tokens: 0, questsCompleted: [] };
    daily.updateHistory(today, { questsCompleted: [...todayHistory.questsCompleted, quest] });
  }, [daily, quests]);

  const handleShowDetails = useCallback((quest: Quest) => setDetailedQuest(quest), []);
  const handleCloseDetails = useCallback(() => setDetailedQuest(null), []);
  const handleCloseEditing = useCallback(() => setEditingQuest(null), []);
  const handleOpenSettings = useCallback(() => setSettingsModalOpen(true), []);
  const handleCloseSettings = useCallback(() => setSettingsModalOpen(false), []);

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  const categoryCounts = quests.activeQuests.reduce(
    (acc, q) => {
      acc[q.category] = (acc[q.category] || 0) + 1;
      return acc;
    },
    {} as Record<string, number>,
  );

  return (
      <div style={{ backgroundColor: 'var(--bg-color)' }}>
      {settings.backgroundImage && (
        <div
          className="fixed inset-0 -z-20"
          style={{
            backgroundImage: `url(${settings.backgroundImage})`,
            backgroundSize: settings.backgroundImageSizing,
            backgroundRepeat: settings.backgroundImageSizing === 'repeat' ? 'repeat' : 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
      )}
      <div 
        className="fixed inset-0 backdrop-blur-sm -z-10"
        style={{ backgroundColor: 'var(--bg-backdrop)'}}
      ></div>

      <div className="p-4 sm:p-6 lg:p-8 font-sans min-h-screen text-slate-800 dark:text-white">
        <div className="max-w-7xl mx-auto flex flex-col" style={{minHeight: 'calc(100vh - 3rem)'}}>
        <Header onOpenSettings={handleOpenSettings} version={version} />

          <main className="mt-8 flex flex-col-reverse lg:flex-row gap-8 flex-1">
            <div className="w-full lg:w-1/3 flex flex-col gap-8">
              <CalendarView history={daily.history} tokenIcon={settings.tokenIcon} />
              <PostTokenRoutinePanel />
              <StatsPanel history={daily.history} />
            </div>
            <div className="w-full lg:w-2/3 flex flex-col gap-8">
              <TokenDisplay
                tokens={daily.tokens}
                tokenIcon={settings.tokenIcon}
                onSpendToken={daily.handleSpendToken}
                dailyChecklistCompleted={daily.dailyChecklistCompleted}
              />
              <QuestSelection
                offeredQuests={quests.offeredQuests}
                categoryOrder={CATEGORY_ORDER}
                onChoose={quests.handleChooseQuest}
                onReject={quests.handleRejectQuest}
                categoryCounts={categoryCounts}
                isMaxQuestsReached={quests.activeQuests.length >= 5}
                onShowDetails={handleShowDetails}
                completedQuestIds={quests.completedQuestIds}
              />
             <ActiveQuestsPanel
              activeQuests={quests.activeQuests}
              onShowDetails={handleShowDetails}
              onCompleteQuest={handleCompleteQuest}
              completedQuestIds={quests.completedQuestIds}
            />
            </div>
          </main>

          <footer className="text-center text-slate-500 dark:text-slate-400 mt-12 pb-4">
            <p>Designed together. Evolving together.</p>
          </footer>
        </div>

      {detailedQuest && (
        <QuestDetailsModal
          quest={detailedQuest}
          onClose={handleCloseDetails}
          isSkillBuilderRepeat={(detailedQuest.category === QuestCategory.SkillBuilder && quests.completedQuestIds.includes(detailedQuest.id))}
        />
      )}

      {editingQuest && (
        <EditQuestModal
          quest={editingQuest}
          onClose={handleCloseEditing}
          onSave={() => {}}
        />
      )}

      {isSettingsModalOpen && (
        <SettingsModal onClose={handleCloseSettings} />
      )}
    </div>
    </div>
  );
};

export default MainScreen;
